import { lazyNamedExport } from "../../../components/molecules/quickCreate/lazyNamedExport";
import { openCreateInNewTab } from "../../../components/molecules/quickCreate/openCreateInNewTab";
import { QuickCreateSelect as SharedQuickCreateSelect } from "../../../components/molecules/quickCreate/QuickCreateSelect";
import { TableQuickCreateSelect } from "../../../components/molecules/quickCreate/TableQuickCreateSelect";
import { FormControlSearchableSelectAPI } from "../../../components/organisms/Form/control";
import { FormControlTableSearchableSelectAPI } from "../../receipt/bill/components/formTableSelectApi/formTableSelectSearchApi";

/**
 * Create * drawers load on first open. Full documents open in a new tab.
 *
 * @const
 * @type {!Object<string, {createLabel: string, Drawer: (*|undefined), path: (string|undefined)}>}
 */
const CREATE_BY_KIND = {
  facility: {
    createLabel: "Create facility",
    Drawer: lazyNamedExport(
      () => import("../../integrationV1/shopify/manage/quickCreate/FacilityQuickCreate"),
      "FacilityQuickCreate",
    ),
  },
  salesRep: {
    createLabel: "Create sales rep",
    Drawer: lazyNamedExport(
      () => import("../../integrationV1/shopify/manage/quickCreate/SalesRepQuickCreate"),
      "SalesRepQuickCreate",
    ),
  },
  taxRate: {
    createLabel: "Create tax rate",
    Drawer: lazyNamedExport(
      () => import("../../integrationV1/shopify/manage/quickCreate/TaxRateQuickCreate"),
      "TaxRateQuickCreate",
    ),
  },
  chartAccount: {
    createLabel: "Create account",
    Drawer: lazyNamedExport(
      () => import("../../integrationV1/shopify/manage/quickCreate/AccountQuickCreate"),
      "AccountQuickCreate",
    ),
  },
  supplier: {
    createLabel: "Create supplier",
    Drawer: lazyNamedExport(
      () => import("../../supplierV2/manage/quickCreate"),
      "SupplierQuickCreate",
    ),
  },
  paymentTerm: {
    createLabel: "Create payment terms",
    Drawer: lazyNamedExport(
      () => import("../../paymentTerm/manage/quickCreate"),
      "PaymentTermQuickCreate",
    ),
  },
  site: {
    createLabel: "Create site",
    Drawer: lazyNamedExport(() => import("../../site/manage/quickCreate"), "SiteQuickCreate"),
  },
  unitOfMeasure: {
    createLabel: "Create UOM",
    Drawer: lazyNamedExport(
      () => import("../../unitOfMeasure/manage/quickCreate"),
      "UnitOfMeasureQuickCreate",
    ),
  },
  product: {
    createLabel: "Create Product",
    Drawer: lazyNamedExport(
      () => import("../../product/Manage/quickCreate/Manage"),
      "ProductQuickCreate",
    ),
  },
  customer: {
    createLabel: "Create Customer",
    Drawer: lazyNamedExport(
      () => import("../../customer/manage/quickCreate"),
      "CustomerQuickCreate",
    ),
  },
  pricelist: {
    createLabel: "Create Price List",
    path: "/sales/pricelist/create",
  },
  billChargeType: {
    createLabel: "Create charge type",
    path: "/master/charge-type/create",
  },
  bom: {
    createLabel: "Create BOM",
    path: "/bom/create",
  },
  workCenter: {
    createLabel: "Create work centre",
    path: "/master/work_center/create",
  },
  saleOrder: {
    createLabel: "Create sales order",
    path: "/sales/order/create",
  },
  purchaseOrder: {
    createLabel: "Create purchase order",
    path: "/orders/purchase/create",
  },
  transferOrder: {
    createLabel: "Create transfer order",
    path: "/transfer_order/create",
  },
};

/**
 * Document Create * select. Maps createKind onto a drawer or a new-tab create.
 *
 * @param {object} props
 * @param {string} props.createKind Drawer or new-tab kind.
 * @param {boolean=} props.table Use the line-table select instead of the header select.
 * @param {boolean=} props.refetchOnOpen
 * @param {string=} props.createLabel
 * @return {*}
 */
export const QuickCreateSelect = ({
  createKind,
  refetchOnOpen = false,
  createLabel,
  table = false,
  ...selectProps
}) => {
  const config = CREATE_BY_KIND[createKind];
  if (!config) throw new Error(`Unknown quick-create kind: ${createKind}`);

  const label = createLabel || config.createLabel;

  if (config.path) {
    const Shared = table ? FormControlTableSearchableSelectAPI : FormControlSearchableSelectAPI;
    return (
      <Shared
        createLabel={label}
        onCreate={() => openCreateInNewTab(config.path)}
        {...(table ? {} : { refetchOnOpen })}
        {...selectProps}
      />
    );
  }

  const Shared = table ? TableQuickCreateSelect : SharedQuickCreateSelect;
  return (
    <Shared
      Drawer={config.Drawer}
      createLabel={label}
      {...(table ? {} : { refetchOnOpen })}
      {...selectProps}
    />
  );
};
